/**
 * Mesh gradient with hand-placed colour points.
 *
 * Paper's static mesh gradient already blends N colour points by inverse
 * distance — exactly the behaviour a point editor needs. The one thing it does
 * not offer is the points themselves: it derives them inside the shader from a
 * seed (`getPosition(i, seed)`), so the author can shuffle the arrangement but
 * never place it.
 *
 * Rather than fork the library, the stock shader is rewritten in one place: the
 * position lookup becomes a `u_points` uniform. Everything else — the wave
 * distortion, the blend sharpness, the grain — stays exactly as upstream wrote
 * it. The dependency is pinned to an exact version, so the two markers below
 * cannot move underneath us without a deliberate upgrade; if they ever do, the
 * build throws instead of silently rendering the seeded arrangement.
 */

/** Colour points the editor offers. The shader array stays at the library's own
 *  maximum so the unrolled loop can never index past its end. */
export const MESH_POINT_LIMIT = 5;

/**
 * A colour point in canvas coordinates: y grows downward, matching the preview,
 * the editor and every other coordinate in the app.
 *
 * The shader's UV origin is at the bottom-left, so its y runs the other way.
 * That flip is confined to `toShaderPoints` — the one place that talks to GLSL —
 * rather than leaking into the editor, where an upside-down drag would be a
 * lasting trap.
 */
export interface MeshPoint {
  x: number;
  y: number;
}

/** All three have to go together: dropping the uniform while leaving the seed
 *  that reads it fails to compile, and the failure surfaces far downstream as a
 *  null program rather than as a shader error. */
const POSITION_UNIFORM_MARKER = 'uniform float u_positions;';
const POSITION_SEED_MARKER = 'float positionSeed = 25. + .33 * u_positions;';
const POSITION_CALL_MARKER = 'vec2 pos = getPosition(i, positionSeed) + mixerGrain;';
/** Flow is strongest here and fades outward; upstream pins it to the middle. */
const FLOW_CENTER_MARKER = 'float radius = smoothstep(0., 1., length(uv - .5));';

export function buildMeshPointsShader(stockShader: string, arraySize: number): string {
  const missing = [
    POSITION_UNIFORM_MARKER,
    POSITION_SEED_MARKER,
    POSITION_CALL_MARKER,
    FLOW_CENTER_MARKER
  ].filter((marker) => !stockShader.includes(marker));
  if (missing.length > 0) {
    throw new Error(
      `Paper mesh gradient shader no longer matches the lookups this build rewrites: ${missing.join(' / ')}`
    );
  }

  return stockShader
    .replace(POSITION_UNIFORM_MARKER, `uniform vec2 u_points[${arraySize}];\nuniform vec2 u_flowCenter;`)
    .replace(POSITION_SEED_MARKER, '')
    .replace(POSITION_CALL_MARKER, 'vec2 pos = u_points[i] + mixerGrain;')
    .replace(FLOW_CENTER_MARKER, 'float radius = smoothstep(0., 1., length(uv - u_flowCenter));');
}

/**
 * The arrangement the stock shader would have produced for a given seed.
 *
 * Ported from upstream's `getPosition` so the Variation slider keeps its old
 * meaning: it seeds a starting layout, which the author then drags. Without
 * this, turning the editor on would have thrown away every arrangement anyone
 * had already dialled in by seed.
 */
export function seededMeshPoints(seed: number, count: number): MeshPoint[] {
  const t = 25 + 0.33 * clamp(seed, 0, 100);
  const points: MeshPoint[] = [];

  for (let i = 0; i < count; i += 1) {
    const a = i * 0.37;
    const b = 0.6 + (i % 3) * 0.3;
    const c = 0.8 + ((i + 1) % 4) * 0.25;
    points.push({
      x: 0.5 + 0.5 * Math.sin(t * b + a),
      // Upstream computes `0.5 + 0.5 * cos(...)` in shader space; flipped here
      // so the seeded layout is stored the same way up as a dragged one, and
      // the two flips cancel by the time it reaches GLSL.
      y: 0.5 - 0.5 * Math.cos(t * c + a * 1.5)
    });
  }

  return points;
}

/** Points travel past the canvas edge on purpose — a colour anchored just
 *  outside is how a corner gets an even wash instead of a visible blob. */
export const MESH_POINT_MIN = -0.35;
export const MESH_POINT_MAX = 1.35;

export function clampMeshPoint(point: MeshPoint): MeshPoint {
  return {
    x: clamp(point.x, MESH_POINT_MIN, MESH_POINT_MAX),
    y: clamp(point.y, MESH_POINT_MIN, MESH_POINT_MAX)
  };
}

/**
 * Canvas coordinates out, GLSL coordinates in.
 *
 * Y is flipped here because the shader's UV origin sits at the bottom-left.
 * Pads to the shader's array size; the loop breaks at u_colorsCount, so the
 * tail is never sampled and only has to be well-formed.
 */
export function toShaderPoints(points: MeshPoint[], arraySize: number): [number, number][] {
  const filled: [number, number][] = [];
  for (let i = 0; i < arraySize; i += 1) {
    const point = points[i];
    filled.push(point ? [point.x, 1 - point.y] : [0.5, 0.5]);
  }
  return filled;
}

/** Single point across the same boundary — see toShaderPoints. */
export function toShaderPoint(point: MeshPoint): [number, number] {
  return [point.x, 1 - point.y];
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
